/* eslint-disable max-len */
/* eslint-disable no-console */
/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/

import * as React from 'react';
import { useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import debounce from 'lodash.debounce';
import {
  useDispatch, useSelector, shallowEqual,
} from 'react-redux';
import { useParams } from 'react-router-dom';
import { ContentContainer } from '@ziynx/components/layout';
import { Heading } from '@ziynx/components/typography';
import Link from '@ziynx/components/links';
import Modal from '@ziynx/components/modals';
import Card from '@ziynx/components/cards';
import Colors from '@ziynx/theme/colors';
import { Form, FormRow } from '@ziynx/components/forms';
import { eventNames } from '@ziynx/analytics';
import Input, { InputAutocomplete } from '@ziynx/components/inputs';
import { Button } from '@ziynx/components/buttons';
import type { VideoUploadConfig, ImageUploadConfig } from '@ziynx/flow-types/companyProfile';
import UploadVideoModal from '@ziynx/pages/Modals/UploadVideo';
import UploadPhotoModal from '@ziynx/pages/Modals/UploadPhoto';
import DestructiveModal from '@ziynx/pages/Modals/DestructiveModal';
import ActionTypes from '@ziynx/action-types';
import {
  withAppManager, useNotification, LoadingSpinner,
  dateToUnixTimestamp, unixTimestampToDate,
} from '@ziynx/components/utilities';
import * as KrispyTokens from '@krispy-tokens/blue';
import type { Viewports } from '@ziynx/flow-types/globals';

type Props = $ReadOnly<{|
  /**
   * Viewport
   */
  viewport: Viewports,
  /**
   * Navigation stack
   */
  history: {
    push: (pathName: string, params?: { from: string }) => void,
    goBack: () => void,
    location: {
      state: {
        from: string,
      }
    },
  },

|}>;

const _modalWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '333px';
  case 'mobileSmall':
    return '240px';
  default:
    return '488px';
  }
};

const modalButtonWidth = (viewport) => {
  switch (viewport) {
  case 'mobile':
    return '50%';
  case 'mobileSmall':
    return '100%';
  default:
    return '40%';
  }
};

const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  ${({ isMobileSmall }) => (isMobileSmall
    ? `padding-top: ${KrispyTokens.KrispySpacingElementsLdXsmall}px; 
      padding-right: ${KrispyTokens.KrispySpacingElementsHdXlarge}px; 
      padding-bottom${KrispyTokens.KrispySpacingElementsLdLarge}px;
      padding-left: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`
    : `paddding-top: ${KrispyTokens.KrispySpacingElementsLdXsmall}px; 
      padding-right: ${KrispyTokens.KrispySpacingElementsLdXsmall}px; 
      paddding-bottom: ${KrispyTokens.KrispySpacingElementsLdLarge}px;
      padding-left: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;`)};
`;

const PageContentWrapper = styled.div`
  width: 100%;
  max-width: 688px;
  fieldset {
    all: unset;
    width: 100%;
  }
`;

const LinkWrapper = styled.div`
  margin-top: ${KrispyTokens.KrispySpacingElementsMdSmall}px;
  margin-bottom: ${KrispyTokens.KrispySpacingElementsMdSmall}px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
  margin-top: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;
  margin-bottom: ${KrispyTokens.KrispySpacingElementsLdXsmall}px;
`;

const StyledButton = styled(Button)`
  width: ${({ isMobileSmall }) => (isMobileSmall ? '100%' : '50%')};
  &:first-of-type {
    ${({ isMobileSmall }) => (isMobileSmall
    ? `margin-bottom: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`
    : `margin-right: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`)}
  }
`;

const CardWrapper = styled.div`
  margin-top: ${KrispyTokens.KrispySpacingElementsMdLarge}px;
`;

const DiscardModal = styled(Modal)`
  padding: 24px;
  height: 180px;
  width: ${({ viewport }) => _modalWidth(viewport)};
`;

const ModalButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
`;

const ModalButton = styled(Button)`
  width: ${({ viewport }) => modalButtonWidth(viewport)};
  ${({ isMobileSmall, firstChild }) => !isMobileSmall && firstChild && 'margin-right: 16px;'}
  ${({ isMobileSmall, firstChild }) => isMobileSmall && firstChild && 'margin-bottom: 16px;'}
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  height: 100%;
`;

const StyledHeading = styled(Heading)`
  padding-bottom: 16px;
`;
/**
 * reducer state
 {
    query: {
      name?: string,
    },
    videoUploadModal: {
      open: boolean,
    }
    imageUploadModal: {
      open: false,
    },
    video: {

    },
    image: {

    }
 }
 */
function reducer(state: ReducerState, action: ActionType) {
  const { type, ...rest } = action;

  switch (type) {
  case 'setSelectedCompany':
    return {
      ...state,
      company: action?.company,
    };
  case 'setOriginalVideo':
    return {
      ...state,
      video: {
        assetId: action?.video?.assetId || action?.video?._id,
        sources: action?.video?.sources,
        destination: 'originalVideo',
        skipCropStep: true,
        uploadReferenceId: action?.video?.uploadReferenceId || 'originalVideo',
        existingVideo: {
          uri: action?.video?.sources?.original?.url,
          thumbnail: {
            uri: action?.video?.sources?.thumbnail?.url,
          },
        },
      },
    };
  case 'setOriginalImage':
    return {
      ...state,
      image: {
        assetId: action?.image?.assetId,
        sources: action?.image?.sources,
        uploadReferenceId: action?.image?.uploadReferenceId,
        existingPhoto: {
          _id: action?.image?._id,
          uri: action?.image?.sources?.original?.url,
        },
      },
      imageUploadModal: {
        open: false,
      },
    };
  case 'setDestructiveModal': {
    return {
      ...state,
      destructiveModal: action?.destructiveModal,
    };
  }
  case 'setState':
    return {
      ...state,
      ...rest,
    };
  default:
    return state;
  }
}

const initialReducerState: ReducerState = {
  fetching: false,
  isSubmitting: false,
  video: {},
  image: {},
  videoUploadModal: {
    open: false,
  },
  imageUploadModal: {
    open: false,
  },
  discardModal: {
    open: false,
  },
  dirtyFields: {
    isDirty: false,
  },
  query: {},
};

const OriginalCreateScreen = (props: ManagedProps<Props>) => {
  const { viewport, history } = props;
  const { id: originalId } = useParams();

  const { notificationDispatch } = useNotification();
  const [state, dispatchReducer] = useReducer(reducer, initialReducerState);

  const isMobileSmall = viewport === 'mobileSmall';
  const goBackTitle = 'Originals List';

  // eslint-disable-next-line max-len
  const originalService = useSelector(({ original = {} }) => (originalId ? (original[originalId] || {}) : original.newOriginal), shallowEqual);
  const companiesService = useSelector(({ search }) => search?.GET_COMPANIES_API || {}, shallowEqual);
  const videoService = useSelector(({ media = {} }) => media, shallowEqual);
  const imageService = useSelector(({ media = {} }) => media, shallowEqual);
  const notificationsService = useSelector(({ notifications = {} }) => notifications, shallowEqual);
  const dispatch: DispatchAction = useDispatch();

  /* Asset Utility functions using reducer and dispatch */
  const _clearCachedImage = () => {
    dispatch({
      type: ActionTypes.media.clearImage,
      mediaUploadReference: {
        uploadReferenceId: originalId || 'originalImage',
      },
    });
  };
  const _clearImageState = () => {
    dispatchReducer({
      type: 'setOriginalImage',
      image: {},
    });
  };
  const _clearCachedVideo = () => {
    dispatch({
      type: ActionTypes.media.clearVideo,
      mediaUploadReference: {
        uploadReferenceId: originalId || 'originalVideo',
      },
    });
  };
  const _clearVideoState = () => {
    dispatchReducer({
      type: 'setOriginalVideo',
      video: {},
    });
  };
  const _clearCachedAssets = () => {
    _clearCachedImage();
    _clearCachedVideo();
  };
  const _clearAssetState = () => {
    _clearImageState();
    _clearVideoState();
  };

  useEffect(() => {
    // Clear image and videos from reducer on load to ensure cleanup occured
    _clearCachedAssets();
    _clearAssetState();
    /**
     * Only run the get if we are editing
     */
    if (originalId) {
      dispatch({
        type: ActionTypes.originals.get.START,
        id: originalId,
      });
    }
  }, [originalId]);

  useEffect(() => {
    const successNotification = notificationsService.variant === 'success' && notificationsService.content.indexOf('Original') > 0;
    if (successNotification) {
      dispatchReducer({
        type: 'setState',
        isSubmitting: false,
      });
    }
    /**
     * Only navigate the user back to the list if we're creating
     */
    if (!originalId && !!state.isSubmitting && successNotification) {
      _clearCachedAssets();
      _clearAssetState();
      history.goBack();
    }
  }, [notificationsService]);

  useEffect(() => {
    if (!!originalService?.company && !state?.company) {
      dispatchReducer({
        type: 'setSelectedCompany',
        company: { text: originalService?.company?.name, ...originalService?.company },
      });
    }
  }, [originalService?.company?._id]);

  useEffect(() => {
    if ((state?.image?.uploadReferenceId && !!imageService[state?.image?.uploadReferenceId]?.assetId) || originalService?.thumbnail) {
      dispatchReducer({
        type: 'setOriginalImage',
        image: {
          ...originalService?.thumbnail,
          ...imageService[state?.image?.uploadReferenceId],
        },
      });
    }
  }, [originalService?.thumbnail, imageService[state?.image?.uploadReferenceId]?.fetching]);

  const _onCompanySearch = (value) => {
    dispatch({
      type: ActionTypes.search.getCompanies.START,
      searching: true,
      paginating: false,
      query: {
        page: 0,
        q: value,
      },
    });
  };

  const INPUT_FIELDS = [
    'title',
    'subtitle',
    'seriesTitle',
    'priority',
    'createdAt',
  ];

  const _onInputChange = (value, updatedInputName) => {
    dispatchReducer({
      type: 'setState',
      dirtyFields: {
        ...state?.dirtyFields,
        isDirty: true,
        [updatedInputName]: {
          value,
        },
      },
    });
  };

  const debouncedCompanies = debounce((e) => _onCompanySearch(e?.target?.value), 300);
  const _debouncedInputChange = debounce((e, inputName) => _onInputChange(e?.target?.value, inputName), 100);

  const _handleCompanyFocus = (e: InputEvent) => {
    debouncedCompanies(e);
  };

  const _handleCompanyScroll = (e: SyntheticEvent<HTMLUListElement>, { hasReachedEnd }) => {
    if (companiesService?.isLastPage) return;
    if (hasReachedEnd) {
      dispatch({
        type: ActionTypes.search.getCompanies.START,
        searching: false,
        fetching: true,
        paginating: true,
        query: {
          page: companiesService.page + 1,
          q: '',
        },
      });
    }
  };

  const getMediaId = (obj) => {
    dispatchReducer({
      type: 'setOriginalVideo',
      video: obj,
    });
  };

  useEffect(() => {
    if (state?.video?.assetId) return;
    if ((!!videoService?.originalVideo?.assetId || originalService?.video) && !state?.video?.existingVideo) {
      const videoObj = {
        ...originalService?.video,
        ...videoService?.originalVideo,
      };
      dispatchReducer({
        type: 'setOriginalVideo',
        video: videoObj,
      });
    }
  }, [videoService?.originalVideo?.assetId, originalService?.video]);

  const getImageId = (obj) => {
    dispatchReducer({
      type: 'setOriginalImage',
      image: obj,
    });
  };

  const _openVideoModal = (uploadConfig: VideoUploadConfig): void => {
    dispatchReducer({
      type: 'setState',
      videoUploadModal: {
        open: true,
      },
      video: uploadConfig,
    });
  };

  const _openImageModal = (uploadConfig: ImageUploadConfig): void => {
    const imageState = {
      ...state?.image,
      ...uploadConfig,
    };
    dispatchReducer({
      type: 'setState',
      imageUploadModal: {
        open: true,
      },
      image: imageState,
    });
  };

  const _updatedAltInputs = () => {
    const companyValue = state?.company?._id;
    const videoValue = state?.video?.assetId || originalService?.video?._id;
    const imageValue = state?.image?.assetId || originalService?.thumbnail?._id;

    if (companyValue !== originalService?.company?._id
      || videoValue !== originalService?.video?._id
      || imageValue !== originalService?.thumbnail?._id) {
      return true;
    }
    return false;
  };

  const _cancelOriginal = () => {
    if (originalService?.fetching || originalService?.isPatching || originalService?.posting) return;

    const dirtyFields = [];
    const updatedAltInputs = _updatedAltInputs();

    INPUT_FIELDS.forEach((field) => {
      if (!state?.dirtyFields[field]) return;
      let dirtyValue = state?.dirtyFields[field]?.value;
      if (field === 'createdAt') dirtyValue = dateToUnixTimestamp(dirtyValue);
      if (field === 'priority' && dirtyValue) dirtyValue = parseInt(dirtyValue, 10);
      /* Check if the field was changed, then changed back */
      if ((dirtyValue && dirtyValue === originalService[field]) || !dirtyValue) return;
      dirtyFields.push(field);
    });
    if ((dirtyFields?.length && dirtyFields?.length > 0) || updatedAltInputs) {
      dispatchReducer({
        type: 'setState',
        discardModal: {
          open: true,
        },
      });
      return;
    }
    _clearCachedAssets();
    _clearAssetState();
    history.goBack();
  };

  const onClickDestructive = (type) => {
    if (type === 'clearImageCache') {
      /* Clear a stuck or unwanted upload */
      dispatch({
        type: ActionTypes.originals.clearImageUploads,
        id: originalId,
      });
      _clearCachedImage();
      _clearImageState();
    } else if (type === 'clearVideoCache') {
      /* Clear a stuck or unwanted upload */
      dispatch({
        type: ActionTypes.originals.clearVideoUploads,
        id: originalId,
      });
      _clearCachedVideo();
      _clearVideoState();
    } else if (type === 'video') {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.removed_original_video,
      });
      // Remove from state and global reducer
      _clearCachedVideo();
      _clearVideoState();
      if (originalId) {
        dispatch({
          type: ActionTypes.originals.patch.START,
          body: [
            {
              op: 'remove',
              path: 'video',
            },
          ],
          id: originalId,
        });
      }
    } else {
      dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.removed_original_thumbnail,
      });
      dispatch({
        type: ActionTypes.originals.patch.START,
        body: [
          {
            op: 'remove',
            path: 'thumbnail',
          },
        ],
        id: originalId,
      });
      // Remove from state and global reducer
      _clearCachedImage();
      _clearImageState();
    }
    dispatchReducer({
      type: 'setDestructiveModal',
      destructiveModal: {
        open: false,
        type: undefined,
      },
    });
  };

  const _onSubmit = async (evt: React.SyntheticEvent) => {
    evt.preventDefault();
    if (originalService?.fetching || originalService?.isPatching) return;

    const target = evt?.target;
    const titleValue = target?.title?.value;
    const subtitleValue = target?.subtitle?.value;
    const seriesTitleValue = target?.seriesTitle?.value;
    const priorityValue = parseInt(target?.priority?.value, 10);
    const companyValue = state?.company?._id;
    const videoValue = state?.video?.assetId || originalService?.video?._id;
    const imageValue = state?.image?.assetId || originalService?.thumbnail?._id;
    const createdAtValue = dateToUnixTimestamp(target?.createdAt?.value);

    if (!titleValue || !subtitleValue || !seriesTitleValue || !videoValue || !imageValue || !createdAtValue) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'error',
          content: 'Please fill out all required fields.',
        },
      });
      return;
    }

    dispatchReducer({
      type: 'setState',
      isSubmitting: true,
    });

    if (!originalId) {
      /**
       * Creating a new Original!
       */

      dispatch({
        type: ActionTypes.originals.post.START,
        body: {
          title: titleValue,
          subtitle: subtitleValue,
          seriesTitle: seriesTitleValue,
          company: companyValue,
          video: videoValue,
          thumbnail: imageValue,
          createdAt: createdAtValue,
          ...(priorityValue ? { priority: priorityValue } : {}),
        },
      });
    } else {
      /**
       * Editing an existing Original!
       */

      const ops = [];

      if (titleValue !== originalService?.title) {
        ops.push({ op: 'add', path: 'title', value: titleValue });
      }

      if (subtitleValue !== originalService?.subtitle) {
        ops.push({ op: 'add', path: 'subtitle', value: subtitleValue });
      }

      if (seriesTitleValue !== originalService?.seriesTitle) {
        ops.push({ op: 'add', path: 'seriesTitle', value: seriesTitleValue });
      }

      if (companyValue !== originalService?.company?._id) {
        ops.push({ op: 'add', path: 'company', value: companyValue });
      }

      if (originalService?.priority && !priorityValue) {
        ops.push({ op: 'remove', path: 'priority' });
      } else if (priorityValue && (priorityValue !== originalService?.priority)) {
        ops.push({ op: 'add', path: 'priority', value: priorityValue });
      }

      if (!originalService?.createdAt || createdAtValue !== originalService?.createdAt) {
        ops.push({ op: 'add', path: 'createdAt', value: createdAtValue });
      }

      if (videoValue !== originalService?.video?._id) {
        ops.push({ op: 'add', path: 'video', value: videoValue });
      }

      if (imageValue !== originalService?.thumbnail?._id) {
        ops.push({ op: 'add', path: 'thumbnail', value: imageValue });
      }

      if (ops?.length < 1) {
        return dispatchReducer({
          type: 'setState',
          isSubmitting: false,
        });
      }

      dispatch({
        type: ActionTypes.originals.patch.START,
        id: originalId,
        body: ops,
      });
    }
  };

  const emptyCompanies = companiesService?.results?.length === 0;

  const imageThumbnail = state?.image?.sources?.original?.url
  || originalService?.thumbnail?.sources?.cropped?.url
  || originalService?.thumbnail?.sources?.original?.url;

  const videoThumbnail = originalService?.video?.sources?.thumbnail?.url
  || state?.video?.sources?.thumbnail?.url
  || state?.video?.existingVideo?.thumbnail?.uri;

  const onWheel = (event: any): any => {
    event?.target?.blur();
  };

  const isLoadingImage = originalService?.fetching
  || imageService?.originalImage?.fetching || imageService[originalId]?.fetching || state?.thumbnail?.fetching;
  const isLoadingVideo = videoService?.originalVideo?.fetching;

  const imageMenuOptionText = !isLoadingImage ? 'Remove image' : 'Cancel';
  const imageModalType = !isLoadingImage ? 'image' : 'clearImageCache';
  const videoMenuOptionText = !isLoadingVideo ? 'Remove video' : 'Cancel';
  const videoModalType = !isLoadingVideo ? 'video' : 'clearVideoCache';

  const videoLinks = [
    {
      children: videoMenuOptionText,
      large: true,
      color: Colors.SecondaryRed,
      onClick: () => {
        dispatchReducer({
          type: 'setDestructiveModal',
          destructiveModal: {
            open: true,
            type: videoModalType,
          },
        });
      },
    },
  ];

  const imageLinks = [
    {
      children: imageMenuOptionText,
      large: true,
      color: Colors.SecondaryRed,
      onClick: () => {
        dispatchReducer({
          type: 'setDestructiveModal',
          destructiveModal: {
            open: true,
            type: imageModalType,
          },
        });
      },
    },
  ];

  if (imageThumbnail && !isLoadingImage) {
    imageLinks.unshift({
      children: 'Edit image',
      large: true,
      onClick: () => {
        dispatch({
          type: ActionTypes.analytics.logEvent.START,
          name: eventNames.changed_original_thumbnail,
        });
        _openImageModal({
          destination: 'originalImage',
          mediaUploadReferenceId: originalId ?? 'originalImage',
        });
      },
    });
  }

  return (
    <>
      <Helmet>
        <title>
          Originals
          {' '}
          { originalId ? 'Edit' : 'Create' }
          {' '}
          | ZIYNX
        </title>
      </Helmet>
      <ContentContainer>
        <UploadVideoModal
          open={state?.videoUploadModal?.open}
          videoUploadConfig={state?.video}
          onClose={() => {
            dispatchReducer({
              type: 'setState',
              videoUploadModal: {
                open: false,
              },
            });
          }}
          getMediaId={getMediaId}
        />
        <UploadPhotoModal
          open={state?.imageUploadModal?.open}
          imageUploadConfig={state?.image}
          onClose={() => {
            dispatchReducer({
              type: 'setState',
              imageUploadModal: {
                open: false,
              },
            });
          }}
          getMediaId={getImageId}
        />
        <DestructiveModal
          heading={{
            text: `Are you sure you want to remove the ${state?.destructiveModal?.type}`,
          }}
          body={{
            text: 'This action cannot be undone.',
          }}
          open={state?.destructiveModal?.open}
          buttonGroup={{
            destructive: {
              children: 'Remove',
              onClick: () => onClickDestructive(state?.destructiveModal?.type),
            },
            primary: {
              children: 'Cancel',
              onClick: () => {
                dispatchReducer({
                  type: 'setDestructiveModal',
                  destructiveModal: {
                    open: false,
                    type: undefined,
                  },
                });
              },
            },
          }}
        />
        <PageWrapper isMobileSmall={isMobileSmall}>
          <PageContentWrapper>
            <LinkWrapper>
              <Link
                variant="caret"
                onClick={_cancelOriginal}
                color={Colors.GrayDark}
              >
                Back to Originals list
              </Link>
            </LinkWrapper>
            { (originalService?.fetching) && (<LoadingWrapper><LoadingSpinner /></LoadingWrapper>) }
            { (!originalService?.fetching) && (
              <>
                <Form
                  formId="original-form"
                  title={`${originalId ? 'Edit' : 'Add'} Original Post`}
                  width="100%"
                  onSubmit={_onSubmit}
                >
                  <fieldset disabled={state?.isSubmitting}>
                    <FormRow>
                      <Input
                        required
                        disabled={originalService?.isPatching || originalService?.fetching}
                        variant="text"
                        autoComplete="off"
                        inputName="title"
                        label="Video Title"
                        onChange={(e) => _debouncedInputChange(e, 'title')}
                        defaultValue={originalService?.title ?? ''}
                      />
                    </FormRow>
                    <FormRow>
                      <Input
                        required
                        disabled={originalService?.isPatching || originalService?.fetching}
                        variant="text"
                        autoComplete="off"
                        inputName="subtitle"
                        label="Video Subtitle"
                        onChange={(e) => _debouncedInputChange(e, 'subtitle')}
                        defaultValue={originalService?.subtitle ?? ''}
                      />
                    </FormRow>
                    <FormRow>
                      <Input
                        disabled={originalService?.isPatching || originalService?.fetching}
                        variant="text"
                        helperText="Ex. 08/01/2023"
                        autoComplete="off"
                        inputName="createdAt"
                        label="Publish Date"
                        labelColor={Colors.GrayDark}
                        onChange={(e) => _debouncedInputChange(e, 'createdAt')}
                        defaultValue={originalService?.createdAt ? unixTimestampToDate(originalService?.createdAt) : undefined}
                      />
                    </FormRow>
                    <FormRow>
                      <Input
                        required
                        disabled={originalService?.isPatching || originalService?.fetching}
                        variant="text"
                        autoComplete="off"
                        inputName="seriesTitle"
                        label="Series Title"
                        onChange={(e) => _debouncedInputChange(e, 'seriesTitle')}
                        defaultValue={originalService?.seriesTitle ?? ''}
                      />
                    </FormRow>
                    <FormRow paddingBottom="32px" height="100%">
                      <InputAutocomplete
                        inputName="company"
                        label="Company"
                        labelColor={Colors.GrayDark}
                        width="auto"
                        showOptionsOnFocus
                        disabled={originalService?.fetching || originalService?.isPatching}
                        isLoading={!emptyCompanies && companiesService?.fetching}
                        isPaginating={companiesService?.paginating || companiesService?.searching}
                        onFocus={_handleCompanyFocus}
                        onScroll={_handleCompanyScroll}
                        onChange={debouncedCompanies}
                        options={companiesService?.results?.map((company) => ({
                          ...company,
                          text: company?.name,
                        }))}
                        onClick={(e, option) => {
                          dispatchReducer({
                            type: 'setSelectedCompany',
                            company: option,
                          });
                        }}
                        selected={state?.company ? [state?.company] : undefined}
                        onSelectedClick={state?.isSubmitting ? () => {}
                          : () => {
                            dispatchReducer({
                              type: 'setSelectedCompany',
                              company: undefined,
                            });
                          }}
                      />
                    </FormRow>
                    <FormRow>
                      <Input
                        disabled={originalService?.isPatching || originalService?.fetching}
                        variant="number"
                        autoComplete="off"
                        inputName="priority"
                        label="Priority"
                        onChange={(e) => _debouncedInputChange(e, 'priority')}
                        defaultValue={originalService?.priority ?? ''}
                        onWheel={onWheel}
                      />
                    </FormRow>
                    <CardWrapper>
                      <Card
                        variant="videoPreview"
                        title="Video"
                        isLoading={isLoadingVideo}
                        showMenuOnLoad
                        image={{
                          imageSource: videoThumbnail,
                        }}
                        link={{
                          children: 'Add a video',
                          onClick: () => {
                            dispatch({
                              type: ActionTypes.analytics.logEvent.START,
                              name: eventNames.added_original_video,
                            });
                            _openVideoModal({
                              destination: 'originalVideo',
                              mediaUploadReferenceId: originalId ?? 'originalVideo',
                              skipCropStep: true,
                            });
                          },
                          disabled: state?.isSubmitting,
                        }}
                        rightAction={{
                          disabled: state?.isSubmitting,
                          popover: {
                            position: 'bottomLeft',
                            links: videoLinks,
                          },
                        }}
                      />
                    </CardWrapper>
                    <CardWrapper>
                      <Card
                        variant="videoPreview"
                        title="Thumbnail Image"
                        isLoading={isLoadingImage}
                        showMenuOnLoad
                        image={{ imageSource: imageThumbnail }}
                        link={{
                          children: 'Add a thumbnail image',
                          onClick: () => {
                            dispatch({
                              type: ActionTypes.analytics.logEvent.START,
                              name: eventNames.added_original_thumbnail,
                            });
                            _openImageModal({
                              destination: 'originalImage',
                              mediaUploadReferenceId: originalId ?? 'originalImage',
                            });
                          },
                          disabled: state?.isSubmitting,
                        }}
                        rightAction={{
                          disabled: state?.isSubmitting,
                          popover: {
                            position: 'bottomLeft',
                            links: imageLinks,
                          },
                        }}
                      />
                    </CardWrapper>
                    <ButtonWrapper isMobileSmall={isMobileSmall}>
                      <StyledButton
                        type="submit"
                        width="50%"
                        disabled={originalService?.fetching || originalService?.isPatching
                          || originalService?.posting || state?.isSubmitting}
                        isLoading={originalService?.isPatching}
                        isMobileSmall={isMobileSmall}
                      >
                        Save
                      </StyledButton>
                      <StyledButton
                        isMobileSmall={isMobileSmall}
                        disabled={state?.isSubmitting}
                        variant="primary"
                        width="50%"
                        inverted
                        onClick={_cancelOriginal}
                      >
                        Cancel
                      </StyledButton>
                    </ButtonWrapper>
                  </fieldset>
                </Form>
              </>
            )}
          </PageContentWrapper>
        </PageWrapper>
        <DiscardModal open={state?.discardModal?.open} viewport={viewport}>
          <ModalContent>
            <StyledHeading small>Are you sure you want to discard your changes?</StyledHeading>
            <ModalButtonWrapper isMobileSmall={isMobileSmall}>
              <ModalButton
                width="100%"
                viewport={viewport}
                isMobileSmall={isMobileSmall}
                firstChild
                onClick={() => {
                  history.goBack();
                }}
              >
                Back to
                {' '}
                {goBackTitle}

              </ModalButton>
              <ModalButton
                viewport={viewport}
                isMobileSmall={isMobileSmall}
                inverted
                width="100%"
                onClick={() => {
                  dispatchReducer({
                    type: 'setState',
                    discardModal: {
                      open: false,
                    },
                  });
                }}
              >
                Cancel
              </ModalButton>
            </ModalButtonWrapper>
          </ModalContent>
        </DiscardModal>
      </ContentContainer>
    </>
  );
};

export default (withAppManager(OriginalCreateScreen): React$ComponentType<ManagedProps<Props>>);
